import { z } from "zod";
import { JobStatusSchema } from "./core";
import { LineItemSchema } from "./sales";

// Email intake: monitored mailboxes + key-phrase rules feed an approval
// queue of AI-parsed work orders. Setup is admin/owner only; the owner can
// share the queue with managers (tenant.intakeShared), but mailbox and rule
// setup never leaves admin+.

export const MailboxProviderSchema = z.enum(["imap", "gmail", "m365"]);
export type MailboxProvider = z.infer<typeof MailboxProviderSchema>;

/** Client-readable mailbox doc — the password lives in a server-only doc. */
export const MailboxSchema = z.object({
  email: z.string().email(),
  provider: MailboxProviderSchema,
  host: z.string(),
  port: z.number().int(),
  username: z.string(),
  status: z.enum(["connected", "error"]).default("connected"),
  lastError: z.string().nullable().default(null),
  lastPollAt: z.number().nullable().default(null),
  /** Highest IMAP UID already scanned, per mailbox UIDVALIDITY. */
  lastUid: z.number().nullable().default(null),
  uidValidity: z.number().nullable().default(null),
  /**
   * "password" = IMAP login (secret doc holds passwordEnc). "oauth" =
   * Microsoft 365 / Outlook signed in through Microsoft (secret doc holds
   * refreshTokenEnc; mail is read with Graph, read-only).
   */
  auth: z.enum(["password", "oauth"]).default("password"),
  /** OAuth mailboxes: receivedDateTime (ms) of the last message handled. */
  graphCursorMs: z.number().nullable().default(null),
  createdByUid: z.string(),
  createdAt: z.number(),
});
export type Mailbox = z.infer<typeof MailboxSchema>;

// Microsoft 365 / Outlook sign-in (Entra app "Clevis", JKB Innovations).
export const M365_CLIENT_ID = "0e8bdc5a-24fa-4ebb-b7aa-efa7e96526db";
export const M365_REDIRECT_URI = "https://app.clevis.pro/api/m365/callback";
/** One Microsoft 365 admin opens this once to approve Clevis for their whole organization. */
export const M365_ADMIN_CONSENT_URL =
  `https://login.microsoftonline.com/common/adminconsent?client_id=${M365_CLIENT_ID}` +
  `&redirect_uri=${encodeURIComponent(M365_REDIRECT_URI)}`;

/** The job type and department a new job gets, by what kind of email made it. */
export const IntakeRouteSchema = z.object({
  jobType: z.string().max(40).nullable().default(null),
  departmentId: z.string().max(64).nullable().default(null),
});
export type IntakeRoute = z.infer<typeof IntakeRouteSchema>;

export const IntakeSettingsSchema = z.object({
  /** Case-insensitive phrases; a message matches when subject or body contains any. */
  phrases: z.array(z.string().min(2).max(120)).max(25).default([]),
  /** By document type (purchase_order, warranty_request…). Unset = Clevis guesses from the job type names. */
  routes: z.record(z.string(), IntakeRouteSchema).default({}),
  updatedAt: z.number(),
});
export type IntakeSettings = z.infer<typeof IntakeSettingsSchema>;

// What a builder email is telling the trade. "completion_notice" = the
// builder reporting the order on it as completed/received (BuildPro "Order
// Complete") — not a start signal. "warranty_request" = service or warranty
// work on a home that's already built (door off track, opener out): a new
// warranty job, even when the lot's original install job exists.
export const IntakeDocTypeSchema = z.enum([
  "purchase_order",
  "start_notice",
  "completion_notice",
  "warranty_request",
  "schedule_change",
  "cancellation",
  "other",
]);
export type IntakeDocType = z.infer<typeof IntakeDocTypeSchema>;

export const INTAKE_DOC_LABELS: Record<IntakeDocType, string> = {
  purchase_order: "Purchase order",
  start_notice: "Start notice",
  completion_notice: "Order complete",
  warranty_request: "Warranty request",
  schedule_change: "Schedule change",
  cancellation: "Cancellation",
  other: "Other",
};
/** The email types that can create jobs (the ones a job type / department default makes sense for). */
export const INTAKE_CREATING_TYPES: IntakeDocType[] = ["purchase_order", "start_notice", "completion_notice", "warranty_request", "other"];

/**
 * The job type a new job from this kind of email gets when the company didn't
 * set one: matched by name from its own job types (Warranty, Production…).
 */
export function guessIntakeJobType(docType: IntakeDocType, jobTypes: string[]): string | null {
  const find = (re: RegExp) => jobTypes.find((t) => re.test(t)) ?? null;
  if (docType === "warranty_request") return find(/warrant/i) ?? find(/service|repair/i);
  if (docType === "purchase_order" || docType === "start_notice" || docType === "completion_notice") {
    return find(/production/i) ?? find(/new\s*construction|builder|install/i);
  }
  return null;
}

export const WorkOrderDraftSchema = z.object({
  builderName: z.string().nullable().default(null),
  community: z.string().nullable().default(null),
  lotNumber: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  poNumber: z.string().nullable().default(null),
  wantedDate: z.string().nullable().default(null),
  title: z.string().default("Work order"),
  scope: z.string().default(""),
  lines: z
    .array(
      z.object({
        description: z.string(),
        quantity: z.number(),
        unitPriceCents: z.number().int().nullable().default(null),
      }),
    )
    .default([]),
  contactName: z.string().nullable().default(null),
  contactPhone: z.string().nullable().default(null),
  confidence: z.enum(["high", "medium", "low"]).default("low"),
  docType: IntakeDocTypeSchema.default("other"),
  /** One line: what the email asks of the trade. */
  summary: z.string().default(""),
  /** YYYY-MM-DD the builder says the order was completed (completion notices). */
  completedDate: z.string().nullable().default(null),
  /** Filled only when one document covers several lots — one entry per lot. */
  lots: z
    .array(
      z.object({
        lotNumber: z.string().nullable().default(null),
        address: z.string().nullable().default(null),
        poNumber: z.string().nullable().default(null),
        wantedDate: z.string().nullable().default(null),
      }),
    )
    .default([]),
});
export type WorkOrderDraft = z.infer<typeof WorkOrderDraftSchema>;

export const IntakeItemStatusSchema = z.enum(["pending", "approved", "dismissed"]);

// The planner's proposal: what to do about the email, one entry per job
// touched (a multi-lot notice proposes one create per lot). The human can
// untick, retarget or edit each before applying. Empty strings / nulls on an
// update mean "leave as is". "create_invoice" rides along with the action
// that closes a job out (a builder completion notice): it drafts the invoice
// from the email's priced lines and shares that action's job.
export const IntakeActionKindSchema = z.enum([
  "create_job",
  "update_job",
  "complete_job",
  "cancel_job",
  "add_note",
  "create_invoice",
  "ignore",
]);
export type IntakeActionKind = z.infer<typeof IntakeActionKindSchema>;

/** An invoice line proposed from the email (same bounds as an invoice's LineItem). */
export const IntakeInvoiceLineSchema = LineItemSchema.pick({ description: true, quantity: true, unitPriceCents: true });
export type IntakeInvoiceLine = z.infer<typeof IntakeInvoiceLineSchema>;

/** What proposed invoice lines add up to before tax (rounded per line, as computeTotals does). */
export const intakeLinesTotalCents = (lines: IntakeInvoiceLine[]) =>
  lines.reduce((sum, l) => sum + Math.round(l.quantity * l.unitPriceCents), 0);

export const IntakeActionSchema = z.object({
  id: z.string(),
  kind: IntakeActionKindSchema,
  /** Existing job this acts on (everything but create_job / ignore). */
  jobId: z.string().nullable().default(null),
  reason: z.string().default(""),
  title: z.string().default(""),
  address: z.string().default(""),
  poNumber: z.string().nullable().default(null),
  lotNumber: z.string().nullable().default(null),
  /** YYYY-MM-DD; scheduled at 8:00 in the tenant's timezone. */
  startDate: z.string().nullable().default(null),
  /** Added to the job's notes (create/update/complete/cancel/add_note). */
  note: z.string().default(""),
  /** create_invoice only: the draft invoice's lines. Its PO is poNumber. */
  lines: z.array(IntakeInvoiceLineSchema).max(200).default([]),
  /**
   * create_job only: create it already done — the builder reported work
   * complete that Clevis had no job for (startDate = the completion day).
   */
  createDone: z.boolean().default(false),
  /** create_invoice only: bill the job that this create_job action (same item) makes, instead of jobId. */
  forActionId: z.string().nullable().default(null),
  /** create_job only: the new job's type (Production, Warranty…) and department. */
  jobType: z.string().max(40).nullable().default(null),
  departmentId: z.string().max(64).nullable().default(null),
});
export type IntakeAction = z.infer<typeof IntakeActionSchema>;

/** Existing jobs the planner considered — also the retarget list in review. */
export const IntakeCandidateSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: JobStatusSchema,
  startAt: z.number().nullable().default(null),
  address: z.string().default(""),
  poNumber: z.string().nullable().default(null),
  why: z.string(),
});
export type IntakeCandidate = z.infer<typeof IntakeCandidateSchema>;

export const IntakeAppliedSchema = z.object({
  actionId: z.string(),
  kind: IntakeActionKindSchema,
  jobId: z.string().nullable(),
  detail: z.string(),
});

/** source.mailboxId of an item uploaded on the Work orders page rather than read from a mailbox. */
export const INTAKE_UPLOAD_MAILBOX_ID = "upload";

export const IntakeItemSchema = z.object({
  source: z.object({
    mailboxId: z.string(),
    mailboxEmail: z.string(),
    messageId: z.string(),
    subject: z.string(),
    from: z.string(),
    receivedAt: z.number().nullable(),
  }),
  matchedPhrases: z.array(z.string()),
  draft: WorkOrderDraftSchema,
  /** Possible duplicates spotted at intake time. */
  dupes: z.object({
    jobs: z.array(z.object({ id: z.string(), title: z.string(), reason: z.string() })).default([]),
    customers: z.array(z.object({ id: z.string(), displayName: z.string() })).default([]),
  }),
  actions: z.array(IntakeActionSchema).default([]),
  candidates: z.array(IntakeCandidateSchema).default([]),
  /** "ai" = the model proposed the actions; "rules" = fallback when it couldn't. */
  planner: z.enum(["ai", "rules"]).nullable().default(null),
  /** Set when the email couldn't be read — the item is queued anyway so nothing is silently dropped. */
  extractError: z.string().nullable().default(null),
  appliedActions: z.array(IntakeAppliedSchema).default([]),
  status: IntakeItemStatusSchema.default("pending"),
  approvedJobId: z.string().nullable().default(null),
  resolvedByUid: z.string().nullable().default(null),
  resolvedAt: z.number().nullable().default(null),
  /**
   * Set when someone uploaded the document on the Work orders page (source
   * mailboxId "upload"). That person can review and apply it there even
   * when the queue isn't shared with managers.
   */
  uploadedByUid: z.string().nullable().default(null),
  /** Sent in by a customer from the portal: who sent it (the portal lists what they sent). */
  portal: z.object({ contactId: z.string(), customerId: z.string(), fileName: z.string().default(""), storagePath: z.string().default("") }).nullable().optional(),
  createdAt: z.number(),
});
export type IntakeItem = z.infer<typeof IntakeItemSchema>;
